import dayjs, { Dayjs } from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

import { Field } from "../../types/TenantInterfaces";
import StrUtils from "../../utils/StrUtils";
dayjs.extend(quarterOfYear);

// Helper function for buildDateFilterOptions
const getDateTimeFilter = (
  field: Field,
  name: string,
  min: Dayjs,
  max: Dayjs
) => {
  return {
    Field: {
      APIName: field?.APIName,
      FieldType: field?.FieldType,
      Name: field?.Name,
    },
    Selected: false,
    Name: name,
    Value: "",
    id: StrUtils.uuidv4(),
    Filter: {
      Field: field?.APIName,
      Method: "between",
      Min: min.toDate().toISOString(),
      Max: max.toDate().toISOString(),
    },
  };
};

/**
 * buildDateFilterOptions - Builds all the Quick Filter options for date time fields
 * @param {object} field - The DateTime field to build quick filters for
 */
export const buildDateFilterOptions = (field: Field | null = null) => {
  if (!field) return [];

  let now = dayjs();
  const filters = [];
  const getFilter = (name: string, mn: Dayjs, mx: Dayjs) =>
    getDateTimeFilter(field, name, mn, mx);

  // ------------- Today, Tommorow, and Yesterday quick filters. -----------
  const startToday = dayjs(now).startOf("day");
  const endToday = dayjs(now).endOf("day");

  const startYesterday = dayjs(now).startOf("day").subtract(1, "day");
  const endYesterday = dayjs(now).endOf("day").subtract(1, "day");

  const startTomorrow = dayjs(now).startOf("day").add(1, "day");
  const endTomorrow = dayjs(now).endOf("day").add(1, "day");

  filters.push(getFilter("Today", startToday, endToday));
  filters.push(getFilter("Tomorrow", startTomorrow, endTomorrow));
  filters.push(getFilter("Yesterday", startYesterday, endYesterday));

  // -------------- This Week, Last Week, and Next Week quick filters ------------
  const startThisWeek = dayjs(now).startOf("day").startOf("week");
  const endThisWeek = dayjs(now).endOf("day").endOf("week");

  const startNextWeek = dayjs(now)
    .startOf("day")
    .startOf("week")
    .add(1, "week");
  const endNextWeek = dayjs(now).endOf("day").endOf("week").add(1, "week");

  const startLastWeek = dayjs(now)
    .startOf("day")
    .startOf("week")
    .subtract(1, "week");
  const endLastWeek = dayjs(now).endOf("day").endOf("week").subtract(1, "week");

  filters.push(getFilter("This Week", startThisWeek, endThisWeek));
  filters.push(getFilter("Next Week", startNextWeek, endNextWeek));
  filters.push(getFilter("Last Week", startLastWeek, endLastWeek));

  // -------------- This Month, Next Month, and Last Month quick filters ------------
  const startThisMonth = dayjs(now).startOf("day").startOf("month");
  const endThisMonth = dayjs(now).endOf("day").endOf("month");

  const startNextMonth = dayjs(now)
    .startOf("day")
    .startOf("month")
    .add(1, "month");
  const endNextMonth = dayjs(now).endOf("day").endOf("month").add(1, "month");

  const startLastMonth = dayjs(now)
    .startOf("day")
    .startOf("month")
    .subtract(1, "month");
  const endLastMonth = dayjs(now)
    .endOf("day")
    .endOf("month")
    .subtract(1, "month");

  filters.push(getFilter("This Month", startThisMonth, endThisMonth));
  filters.push(getFilter("Next Month", startNextMonth, endNextMonth));
  filters.push(getFilter("Last Month", startLastMonth, endLastMonth));

  // -------------- This Quarter, Next Quarter, and Last Quarter quick filters ------------
  const startThisQuarter = dayjs(now).startOf("day").startOf("quarter");
  const endThisQuarter = dayjs(now).endOf("day").endOf("quarter");

  const startNextQuarter = dayjs(now)
    .startOf("day")
    .startOf("quarter")
    .add(1, "quarter");
  const endNextQuarter = dayjs(now)
    .endOf("day")
    .endOf("quarter")
    .add(1, "quarter");

  const startLastQuarter = dayjs(now)
    .startOf("day")
    .startOf("quarter")
    .subtract(1, "quarter");
  const endLastQuarter = dayjs(now)
    .endOf("day")
    .endOf("quarter")
    .subtract(1, "quarter");

  filters.push(getFilter("This Quarter", startThisQuarter, endThisQuarter));
  filters.push(getFilter("Next Quarter", startNextQuarter, endNextQuarter));
  filters.push(getFilter("Last Quarter", startLastQuarter, endLastQuarter));

  // -------------- This Year, Next Year, and Last Year quick filters ------------
  const startThisYear = dayjs(now).startOf("day").startOf("year");
  const endThisYear = dayjs(now).endOf("day").endOf("year");

  const startNextYear = dayjs(now)
    .startOf("day")
    .startOf("year")
    .add(1, "year");
  const endNextYear = dayjs(now).endOf("day").endOf("year").add(1, "year");

  const startLastYear = dayjs(now)
    .startOf("day")
    .startOf("year")
    .subtract(1, "year");
  const endLastYear = dayjs(now).endOf("day").endOf("year").subtract(1, "year");

  filters.push(getFilter("This Year", startThisYear, endThisYear));
  filters.push(getFilter("Next Year", startNextYear, endNextYear));
  filters.push(getFilter("Last Year", startLastYear, endLastYear));

  // ---------------- Next 7 Days, Past 7 Days quick filters --------------
  const next7Start = dayjs(now).startOf("day");
  const next7End = dayjs(now).endOf("day").add(7, "days");

  const last7Start = dayjs(now).startOf("day").subtract(7, "days");
  const last7End = dayjs(now).endOf("day");

  filters.push(getFilter("Next 7 Days", next7Start, next7End));
  filters.push(getFilter("Last 7 Days", last7Start, last7End));

  // ---------------- Next 30 Days, Past 30 Days quick filters --------------
  const next30Start = dayjs(now).startOf("day");
  const next30End = dayjs(now).endOf("day").add(30, "days");

  const last30Start = dayjs(now).startOf("day").subtract(30, "days");
  const last30End = dayjs(now).endOf("day");

  filters.push(getFilter("Next 30 Days", next30Start, next30End));
  filters.push(getFilter("Last 30 Days", last30Start, last30End));

  // ---------------- Next 90 Days, Past 90 Days quick filters --------------
  const next90Start = dayjs(now).startOf("day");
  const next90End = dayjs(now).endOf("day").add(90, "days");

  const last90Start = dayjs(now).startOf("day").subtract(90, "days");
  const last90End = dayjs(now).endOf("day");

  filters.push(getFilter("Next 90 Days", next90Start, next90End));
  filters.push(getFilter("Last 90 Days", last90Start, last90End));

  // ----------------- Future dates ------------------
  filters.push({
    Field: {
      APIName: field?.APIName,
      FieldType: field?.FieldType,
      Name: field?.Name,
    },
    Selected: false,
    Name: "Future Dates",
    Value: "",
    id: StrUtils.uuidv4(),
    Filter: {
      Field: field?.APIName,
      Method: "gt",
      Value: now.toDate().toISOString(),
    },
  });

  // ------------------ Past dates ------------------
  filters.push({
    Field: {
      APIName: field?.APIName,
      FieldType: field?.FieldType,
      Name: field?.Name,
    },
    Selected: false,
    Name: "Past Dates",
    Value: "",
    id: StrUtils.uuidv4(),
    Filter: {
      Field: field?.APIName,
      Method: "lt",
      Value: now.toDate().toISOString(),
    },
  });

  return filters;
};
